// Cookie consent: the categories, the storage key and the helpers that
// read/write the student's choice.
//
// The site sets very few cookies of its own:
//   - the sign-in session (Clerk) and the anonymous session cookie
//     (backend) are strictly necessary: without them nothing works;
//   - Google Tag Manager sets the analytics cookies;
//   - the ad cookies only exist on the free plan (the ads are what pay
//     for it), so the category is irrelevant for a paying student.
//
// The choice lives in localStorage (it never leaves the device) and the
// legacy `showBanner` key is migrated on the first read.

export const CONSENT_STORAGE_KEY = 'cookie_consent';
export const LEGACY_BANNER_KEY = 'showBanner';
export const CONSENT_VERSION = 1;

// `necessary` is always on: it covers the sign-in session, the anonymous
// session and the security cookies. The student cannot switch it off.
export const CONSENT_CATEGORIES = [
  'necessary',
  'analytics',
  'ads',
  'preferences',
];

export const ALWAYS_ON_CATEGORIES = ['necessary'];

// What each category unlocks, in the Google Consent Mode v2 vocabulary
// (so the GTM container can gate its own tags).
export const CONSENT_SIGNALS = {
  necessary: ['security_storage'],
  analytics: ['analytics_storage'],
  ads: ['ad_storage', 'ad_user_data', 'ad_personalization'],
  preferences: ['functionality_storage', 'personalization_storage'],
};

export const emptyConsent = () => ({
  version: CONSENT_VERSION,
  decidedAt: null,
  necessary: true,
  analytics: false,
  ads: false,
  preferences: false,
  restricted: false,
});

const isBool = (value) => value === true;

// Reads the stored choice. The legacy `showBanner: "false"` (the old
// one-button banner) counts as "accepted everything": the student had
// already agreed to the cookies.
export const readConsent = () => {
  const fallback = emptyConsent();
  let raw = null;
  try {
    raw = localStorage.getItem(CONSENT_STORAGE_KEY);
  } catch (_) {
    return fallback;
  }
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return {
          ...fallback,
          version: CONSENT_VERSION,
          decidedAt: parsed.decidedAt || null,
          necessary: true,
          analytics: isBool(parsed.analytics),
          ads: isBool(parsed.ads),
          preferences: isBool(parsed.preferences),
          restricted: isBool(parsed.restricted),
        };
      }
    } catch (_) {
      // corrupt value: fall through to the default
    }
    return fallback;
  }
  let legacy = null;
  try {
    legacy = localStorage.getItem(LEGACY_BANNER_KEY);
  } catch (_) {
    return fallback;
  }
  if (legacy === 'false') {
    return {
      ...fallback,
      decidedAt: null,
      analytics: true,
      ads: true,
      preferences: true,
    };
  }
  return fallback;
};

export const writeConsent = (consent) => {
  const value = {
    version: CONSENT_VERSION,
    decidedAt: new Date().toISOString(),
    necessary: true,
    analytics: isBool(consent.analytics),
    ads: isBool(consent.ads),
    preferences: isBool(consent.preferences),
    restricted: isBool(consent.restricted),
  };
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(value));
    // The old banner key is dead: remove it so it never lies.
    localStorage.removeItem(LEGACY_BANNER_KEY);
  } catch (_) {
    // private mode: the choice is kept in memory for this visit only
  }
  return value;
};

// The Google Consent Mode v2 signals for the current choice.
export const consentSignals = (consent) => {
  const signals = {};
  CONSENT_CATEGORIES.forEach((category) => {
    CONSENT_SIGNALS[category].forEach((signal) => {
      signals[signal] = isBool(consent[category]) ? 'granted' : 'denied';
    });
  });
  return signals;
};

// The ads only exist on the free plan: a paying student never gets them,
// so the category is not offered (and stays off).
export const adsApply = (plan) => !plan || plan === 'free' || plan === 'trial';

export const planAllowsAds = adsApply;
